/**
 * FloatingMockup - CSS-only device mockups for landing page
 */

import { Bell, TrendingDown, Check, MapPin } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface NotificationProps {
  title: string;
  message: string;
  type: 'success' | 'alert' | 'price';
  delay?: number;
}

function Notification({ title, message, type, delay = 0 }: NotificationProps) {
  const colors = {
    success: 'bg-green-500',
    alert: 'bg-accent',
    price: 'bg-primary-600',
  };

  const icons = {
    success: <Check className="w-4 h-4 text-white" />,
    alert: <Bell className="w-4 h-4 text-white" />,
    price: <TrendingDown className="w-4 h-4 text-white" />,
  };

  return (
    <div
      className={`absolute bg-white rounded-xl shadow-xl border border-gray-100 p-3 animate-float`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[type]}`}>
          {icons[type]}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500">{message}</p>
        </div>
      </div>
    </div>
  );
}

interface PhoneMockupProps {
  className?: string;
}

export function PhoneMockup({ className = '' }: PhoneMockupProps) {
  const { t } = useLanguage();
  return (
    <div className={`relative ${className}`}>
      {/* Phone Frame */}
      <div className="relative w-64 h-[520px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-gray-900 rounded-b-2xl z-10" />

        {/* Screen */}
        <div className="relative w-full h-full bg-gradient-to-b from-primary-50 to-white rounded-[2.5rem] overflow-hidden">
          {/* Status Bar */}
          <div className="flex items-center justify-between px-6 pt-8 pb-4">
            <span className="text-xs font-medium text-gray-600">9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-4 h-2 bg-gray-400 rounded-sm" />
              <div className="w-6 h-3 bg-gray-400 rounded-sm" />
            </div>
          </div>

          {/* App Header */}
          <div className="px-5 pb-4">
            <h3 className="text-xl font-bold text-gray-900">{t('mockup.phone.watchlist', 'My Watchlist')}</h3>
            <p className="text-xs text-gray-500">{t('mockup.phone.itemsTracked', '3 items tracked')}</p>
          </div>

          {/* Product Cards */}
          <div className="px-4 space-y-3">
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🪑</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{t('mockup.phone.kallaxShelf', 'KALLAX Shelf')}</p>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-bold text-green-600">$79</span>
                    <span className="text-xs text-gray-400 line-through">$99</span>
                  </div>
                </div>
                <div className="px-2 py-1 bg-green-100 rounded-full">
                  <span className="text-xs font-semibold text-green-600">-20%</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🛋️</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{t('mockup.phone.kivikSofa', 'KIVIK Sofa')}</p>
                  <div className="flex items-center gap-1 text-accent">
                    <Bell className="w-3 h-3" />
                    <span className="text-xs font-medium">{t('mockup.phone.backInStock', 'Back in stock!')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🛏️</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{t('mockup.phone.malmBedFrame', 'MALM Bed Frame')}</p>
                  <div className="flex items-center gap-1 text-gray-500">
                    <MapPin className="w-3 h-3" />
                    <span className="text-xs">{t('mockup.phone.checkingStores', 'Checking 3 stores...')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Notifications */}
      <div className="absolute -top-4 -left-8">
        <Notification
          title={t('mockup.notification.priceDrop', 'Price Drop!')}
          message={t('mockup.notification.kallaxPrice', 'KALLAX is now $79')}
          type="price"
          delay={0}
        />
      </div>
      <div className="absolute -top-4 -right-8">
        <Notification
          title={t('mockup.notification.backInStockTitle', 'Back in Stock')}
          message={t('mockup.notification.kivikAvailable', 'KIVIK Sofa is available')}
          type="alert"
          delay={500}
        />
      </div>
    </div>
  );
}

interface BrowserMockupProps {
  className?: string;
}

export function BrowserMockup({ className = '' }: BrowserMockupProps) {
  const { t } = useLanguage();
  return (
    <div className={`relative ${className}`}>
      {/* Browser Frame */}
      <div className="w-80 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
        {/* Browser Header */}
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 border-b border-gray-200">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 mx-2">
            <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 border border-gray-200">
              {t('mockup.browser.urlBar', 'ikea.com/us/en/p/kallax...')}
            </div>
          </div>
        </div>

        {/* Extension Popup */}
        <div className="p-4 bg-gradient-to-b from-primary-50 to-white">
          {/* Extension Header */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">R</span>
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">RUMIO</p>
              <p className="text-xs text-gray-500">{t('mockup.browser.priceTracker', 'Price Tracker')}</p>
            </div>
          </div>

          {/* Price Chart Preview */}
          <div className="bg-white rounded-lg p-3 border border-gray-100 mb-3">
            <p className="text-xs text-gray-500 mb-2">{t('mockup.browser.priceHistory', 'Price History')}</p>
            <div className="flex items-end gap-1 h-12">
              <div className="w-4 bg-primary-200 rounded-t" style={{ height: '60%' }} />
              <div className="w-4 bg-primary-200 rounded-t" style={{ height: '70%' }} />
              <div className="w-4 bg-primary-200 rounded-t" style={{ height: '50%' }} />
              <div className="w-4 bg-primary-200 rounded-t" style={{ height: '80%' }} />
              <div className="w-4 bg-primary-200 rounded-t" style={{ height: '65%' }} />
              <div className="w-4 bg-green-500 rounded-t" style={{ height: '40%' }} />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">{t('mockup.browser.thirtyDays', '30 days')}</span>
              <span className="text-xs font-semibold text-green-600">{t('mockup.browser.lowest', 'Lowest!')}</span>
            </div>
          </div>

          {/* Track Button */}
          <button className="w-full py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors">
            {t('mockup.browser.trackProduct', 'Track This Product')}
          </button>
        </div>
      </div>

      {/* Floating Alert */}
      <div className="absolute -bottom-6 -left-8">
        <Notification
          title={t('mockup.notification.tracked', 'Tracked!')}
          message={t('mockup.notification.willNotify', "We'll notify you")}
          type="success"
          delay={300}
        />
      </div>
    </div>
  );
}

export default {
  PhoneMockup,
  BrowserMockup,
};
